// BotRadar.jsx
// ══════════════════════════════════════════════
// מכ״מ זיהוי בוטים - אליפסה עם נקודות מסתובבות
// ══════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import "../../../styles/navbar/CyberRoom/BotRadar.css";
import { useMamdani } from "../../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

const TYPE_COLOR = {
  DDoS:        "#ff8c00",
  APT:         "#ff2244",
  Wiper:       "#ff4400",
  Ransom:      "#cc00ff",
  Proxy:       "#ffd700",
  Scan:        "#00cfff",
  Botnet:      "#ff8c00",
  BOT:         "#ff2244",
  LIKELY_BOT:  "#ff7a00",
  SUSPICIOUS:  "#ffd600",
  REVIEW:      "#7eceff",
};

function threatColor(avgThreat) {
  if (avgThreat >= 75) return "#ff2244";
  if (avgThreat >= 55) return "#ff8c00";
  return "#ffd700";
}

export default function BotRadar() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const dotsRef   = useRef([]);
  const [stats, setStats] = useState({ scans: 0, ddos: 0, bots: 0 });
  const [lastAnalyzed, setLastAnalyzed] = useState(null);
  const { mamdaniMode: mm, data } = useMamdani();

  // ── שליפת נתונים ──
  useEffect(() => {
    if (mm) {
      // מוד ממדאני — בנה נקודות מהבוטים שלנו
      const bots = data.allBots.slice(0, 15);
      const dots = bots.map((b, i) => ({
        angle: (i / bots.length) * Math.PI * 2,
        ring:  0.3 + (b.bot_score / 100) * 0.6,
        speed: 0.0001 + (b.bot_score / 100) * 0.0004,
        color: TYPE_COLOR[b.bot_type] || "#ff8c00",
        size:  3 + (b.bot_score / 100) * 4,
        type:  b.bot_type,
        label: b.username,
      }));
      dotsRef.current = dots;
      setStats({
        scans: data.allBots.filter(b => b.anti_israel_count > 0).length,
        ddos:  data.allBots.filter(b => b.is_coordinated).length,
        bots:  data.allBots.length,
      });
      return;
    }

    async function fetchData() {
      try {
        const res  = await fetch(`${API_BASE}/api/cyber/threat-map`);
        const json = await res.json();
        if (!json.success) return;

        const dots = json.threats.slice(0, 12).map((t, i) => {
          const angle = (i / 12) * Math.PI * 2;
          const ring  = 0.3 + (Math.min(t.avg_threat, 100) / 100) * 0.6;
          return {
            angle,
            ring,
            speed: 0.0002 + Math.random() * 0.0003,
            color: threatColor(t.avg_threat),
            size:  3 + Math.min(t.bot_count / 500, 4),
          };
        });
        dotsRef.current = dots;

        const bots = json.threats.reduce((s,t) => s + t.bot_count, 0);
        setStats({ bots });
        setLastAnalyzed(json.last_analyzed || null);

      } catch (err) {
        console.error("BotRadar fetch error:", err);
      }
    }
    fetchData();
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);
  }, [mm, data.allBots]);

  // ── אנימציה ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function draw(ts) {
      const w  = canvas.width;
      const h  = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      const rx = w * 0.42;
      const ry = h * 0.38;

      ctx.clearRect(0, 0, w, h);

      // ── רקע אליפסות ──
      [1, 0.66, 0.33].forEach(scale => {
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx * scale, ry * scale, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0,200,255,0.12)`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      // ── קו סריקה מסתובב ──
      const scanAngle = (ts * 0.0008) % (Math.PI * 2);
      const gradient  = ctx.createLinearGradient(
        cx, cy,
        cx + Math.cos(scanAngle) * rx,
        cy + Math.sin(scanAngle) * ry
      );
      gradient.addColorStop(0, "rgba(0,255,100,0.0)");
      gradient.addColorStop(0.7, "rgba(0,255,100,0.0)");
      gradient.addColorStop(1, "rgba(0,255,100,0.7)");

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(
        cx + Math.cos(scanAngle) * rx,
        cy + Math.sin(scanAngle) * ry
      );
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.stroke();

      // ── Sweep גלאי ──
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, Math.max(rx, ry), scanAngle - 0.6, scanAngle);
      ctx.closePath();
      ctx.fillStyle = "rgba(0,255,100,0.04)";
      ctx.fill();

      // ── נקודת מרכז ──
      const centerGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 12);
      centerGlow.addColorStop(0, "rgba(0,255,100,0.9)");
      centerGlow.addColorStop(1, "transparent");
      ctx.beginPath();
      ctx.arc(cx, cy, 12, 0, Math.PI * 2);
      ctx.fillStyle = centerGlow;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.fillStyle = "#00ff64";
      ctx.fill();

      // ── נקודות בוטים ──
      dotsRef.current.forEach((dot, i) => {
        // סיבוב איטי
        dot.angle += dot.speed;

        const ex = cx + Math.cos(dot.angle) * rx * dot.ring;
        const ey = cy + Math.sin(dot.angle) * ry * dot.ring;

        // בדוק אם הקו עובר קרוב - flash!
        const angleDiff = ((dot.angle - scanAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
        const flash = angleDiff < 0.3 ? (1 - angleDiff / 0.3) : 0;

        const r = dot.size + flash * 3;

        const g = ctx.createRadialGradient(ex, ey, 0, ex, ey, r * 3);
        g.addColorStop(0, `${dot.color}${Math.floor((0.6 + flash * 0.4) * 255).toString(16).padStart(2,"0")}`);
        g.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(ex, ey, r * 3, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(ex, ey, r, 0, Math.PI * 2);
        ctx.fillStyle = dot.color;
        ctx.globalAlpha = 0.7 + flash * 0.3;
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      animRef.current = requestAnimationFrame(draw);
    }

    const ro = new ResizeObserver(() => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    });
    ro.observe(canvas);
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    animRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animRef.current); ro.disconnect(); };
  }, []);

  return (
    <div className="br-wrapper">
      <div className="br-header">
        <span className="br-title">מכ״מ זיהוי בוטים</span>
        <span className="br-updated">
          {lastAnalyzed
            ? `עודכן: ${new Date(lastAnalyzed).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
            : "—"}
        </span>
      </div>

      <div className="br-canvas-wrap">
        <canvas ref={canvasRef} className="br-canvas" />
      </div>

      <div className="br-footer">
        {mm ? (
          <>
            <span className="br-type"><span className="br-dot-small scan" />Anti-Israel <strong>{stats.scans}</strong></span>
            <span className="br-type"><span className="br-dot-small ddos" />Coordinated <strong>{stats.ddos}</strong></span>
            <span className="br-type"><span className="br-dot-small bots" />Total Bots <strong>{stats.bots}</strong></span>
          </>
        ) : (
          <span className="br-type"><span className="br-dot-small bots" />Bots <strong>{stats.bots.toLocaleString()}</strong></span>
        )}
      </div>
    </div>
  );
}